import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vendedorService, Vendedor, VendedorRequest, SITUACOES_VENDEDOR } from '../../services/vendedor';
import SelectPraca from '../praca/SelectPraca';

interface VendedorFormProps {
  isEdit?: boolean;
}

const VendedorForm: React.FC<VendedorFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [formData, setFormData] = React.useState({
    cpf: '',
    nome: '',
    dataNascimento: '',
    telefone1: '',
    telefone2: '',
    rg: '',
    codPraca: 0,
    cep: '',
    cidade: '',
    bairro: '',
    rua: '',
    numero: '',
    situacao: '1',
    uf: '',
    totalDivida: 0,
  });
  const [errors, setErrors] = React.useState({
    cpf: '',
    nome: '',
    dataNascimento: '',
    telefone1: '',
    codPraca: '',
    totalDivida: '',
  });

  // Buscar vendedor para edição
  const { data: vendedor, isLoading } = useQuery({
    queryKey: ['vendedor', id],
    queryFn: () => vendedorService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: VendedorRequest) => 
      isEdit ? vendedorService.editarVendedor(data) : vendedorService.salvarNovoVendedor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendedores'] });
      navigate('/vendedores');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar vendedor:', error);
    },
  });

  useEffect(() => {
    if (vendedor && isEdit) {
      const codPracaValue = Number(vendedor.idPraca) || 0;
      const situacaoInicial = obterIdSituacao(vendedor);
      
      setFormData({
        cpf: vendedor.cpf,
        nome: vendedor.nome,
        dataNascimento: formatarData(vendedor.dataNascimento),
        telefone1: vendedor.telefone1,
        telefone2: vendedor.telefone2 || '',
        rg: vendedor.rg || '',
        codPraca: codPracaValue,
        cep: vendedor.cep || '',
        cidade: vendedor.cidade || '',
        bairro: vendedor.bairro || '',
        rua: vendedor.rua || '',
        numero: vendedor.numero || '',
        situacao: situacaoInicial,
        uf: vendedor.uf || '',
        totalDivida: vendedor.totalDivida || 0,
      });
    }
  }, [vendedor, isEdit]);

  const obterIdSituacao = (v: Vendedor): string => {
    if (v.situacaoId) {
      return String(v.situacaoId);
    }
    const encontrada = SITUACOES_VENDEDOR.find(s =>
      s.descricao.toUpperCase() === (v.situacao || '').toUpperCase()
    );
    return encontrada ? String(encontrada.id) : '1';
  };

  // Função para formatar data para dd/mm/yyyy
  const formatarData = (data: string | null | undefined): string => {
    if (!data) return '';
    
    if (data.includes('/')) {
      const partes = data.split('/');
      if (partes.length === 3) {
        const dia = partes[0].padStart(2, '0');
        const mes = partes[1].padStart(2, '0');
        const ano = partes[2];
        if (ano.length === 4) {
          return `${dia}/${mes}/${ano}`;
        }
      }
    }
    
    if (data.includes('T') || data.includes('-')) {
      const dataObj = new Date(data);
      if (!isNaN(dataObj.getTime())) {
        const dia = dataObj.getDate().toString().padStart(2, '0');
        const mes = (dataObj.getMonth() + 1).toString().padStart(2, '0');
        const ano = dataObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      }
    }
    
    return data;
  };

  const validateForm = (): boolean => {
    const newErrors = {
      cpf: '',
      nome: '',
      dataNascimento: '',
      telefone1: '',
      codPraca: '',
      totalDivida: '',
    };
    let isValid = true;

    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
      isValid = false;
    } else if (!validarCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
      isValid = false;
    }

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
      isValid = false;
    } else if (formData.nome.length > 100) {
      newErrors.nome = 'Nome não deve possuir mais que 100 caracteres';
      isValid = false;
    }

    if (!formData.dataNascimento.trim()) {
      newErrors.dataNascimento = 'Data de nascimento é obrigatória';
      isValid = false;
    }

    if (!formData.telefone1.trim()) {
      newErrors.telefone1 = 'Telefone principal é obrigatório';
      isValid = false;
    }

    if (!formData.codPraca) {
      newErrors.codPraca = 'Praça é obrigatória';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validarCPF = (cpf: string): boolean => {
    cpf = cpf.replace(/[^\d]/g, '');
    if (cpf.length !== 11) return false;
    
    // Validação básica de CPF
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    
    let soma = 0;
    let resto;
    
    for (let i = 1; i <= 9; i++) {
      soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(9, 10))) return false;
    
    soma = 0;
    for (let i = 1; i <= 10; i++) {
      soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(10, 11))) return false;
    
    return true;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Formatar CPF
    if (field === 'cpf') {
      value = value.replace(/[^\d]/g, '');
      if (value.length <= 11) {
        value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
    }
    
    // Formatar Telefone
    if (field === 'telefone1' || field === 'telefone2') {
      value = value.replace(/[^\d]/g, '');
      if (value.length <= 11) {
        value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      }
    }
    
    // Formatar CEP
    if (field === 'cep') {
      value = value.replace(/[^\d]/g, '');
      if (value.length <= 8) {
        value = value.replace(/(\d{5})(\d{3})/, '$1-$2');
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const buscaCep = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            rua: data.logradouro || '',
            bairro: data.bairro || '',
            cidade: data.localidade || '',
            uf: data.uf || '',
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const handleSelectChange = (field: string) => (e: any) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Converter data de nascimento para formato ISO (yyyy-MM-dd)
      const dataNascimentoISO = converterDataParaISO(formData.dataNascimento);

      const vendedorData: VendedorRequest = {
        id: isEdit ? Number(id) : undefined,
        cpf: formData.cpf.replace(/[^\d]/g, ''),
        nome: formData.nome.trim(),
        dataNascimento: dataNascimentoISO,
        telefone1: formData.telefone1.replace(/[^\d]/g, ''),
        telefone2: formData.telefone2.replace(/[^\d]/g, ''),
        rg: formData.rg.trim(),
        codPraca: formData.codPraca,
        cep: formData.cep.replace(/[^\d]/g, ''),
        cidade: formData.cidade.trim(),
        bairro: formData.bairro.trim(),
        rua: formData.rua.trim(),
        numero: formData.numero.trim(),
        situacao: formData.situacao,
        uf: formData.uf.trim(),
        totalDivida: parseFloat(formData.totalDivida.toString()) || 0,
      };

      saveMutation.mutate(vendedorData);
    }
  };

  // Função para converter data do formato brasileiro (dd/MM/yyyy) para ISO (yyyy-MM-dd)
  const converterDataParaISO = (data: string): string => {
    if (!data) return '';

    // Se já estiver no formato ISO, retorna como está
    if (data.includes('-') && data.split('-')[0].length === 4) {
      return data;
    }

    // Converte de dd/MM/yyyy para yyyy-MM-dd
    const partes = data.split('/');
    if (partes.length === 3) {
      const dia = partes[0].padStart(2, '0');
      const mes = partes[1].padStart(2, '0');
      const ano = partes[2];
      if (ano.length === 4) {
        return `${ano}-${mes}-${dia}`;
      }
    }

    return data;
  };

  const handleClear = () => {
    setFormData({
      cpf: '',
      nome: '',
      dataNascimento: '',
      telefone1: '',
      telefone2: '',
      rg: '',
      codPraca: 0,
      cep: '',
      cidade: '',
      bairro: '',
      rua: '',
      numero: '',
      situacao: '1',
      uf: '',
      totalDivida: 0,
    });
    setErrors({
      cpf: '',
      nome: '',
      dataNascimento: '',
      telefone1: '',
      codPraca: '',
      totalDivida: '',
    });
  };

  const handleBack = () => {
    navigate('/vendedores');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Dados Principais */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Dados Principais
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Situação</InputLabel>
              <Select
                value={formData.situacao}
                onChange={handleSelectChange('situacao')}
                label="Situação"
              >
                {SITUACOES_VENDEDOR.map((situacao) => (
                  <MenuItem key={situacao.id} value={String(situacao.id)}>
                    {situacao.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="CPF"
              value={formData.cpf}
              onChange={handleInputChange('cpf')}
              error={!!errors.cpf}
              helperText={errors.cpf}
              placeholder="###.###.###-##"
              slotProps={{
                htmlInput: { maxLength: 14 }
              }}
              required
            />
            
            <TextField
              fullWidth
              label="Total Dívida"
              value={formData.totalDivida}
              onChange={handleInputChange('totalDivida')}
              error={!!errors.totalDivida}
              helperText={errors.totalDivida}
              type="number"
              slotProps={{
                htmlInput: { min: 0, step: 0.01 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Nome"
              value={formData.nome}
              onChange={handleInputChange('nome')}
              error={!!errors.nome}
              helperText={errors.nome}
              slotProps={{
                htmlInput: { maxLength: 100 }
              }}
              required
            />
            
            <TextField
              fullWidth
              label="RG"
              value={formData.rg}
              onChange={handleInputChange('rg')}
              slotProps={{
                htmlInput: { maxLength: 20 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Telefone Principal"
              value={formData.telefone1}
              onChange={handleInputChange('telefone1')}
              error={!!errors.telefone1}
              helperText={errors.telefone1}
              placeholder="(##) #####-####"
              slotProps={{
                htmlInput: { maxLength: 15 }
              }}
              required
            />
            
            <TextField
              fullWidth
              label="Telefone Secundário"
              value={formData.telefone2}
              onChange={handleInputChange('telefone2')}
              placeholder="(##) #####-####"
              slotProps={{
                htmlInput: { maxLength: 15 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Data de Nascimento"
              value={formData.dataNascimento}
              onChange={handleInputChange('dataNascimento')}
              error={!!errors.dataNascimento}
              helperText={errors.dataNascimento}
              placeholder="DD/MM/AAAA"
              slotProps={{
                htmlInput: { maxLength: 10 }
              }}
              required
            />
            
            <SelectPraca
              value={formData.codPraca}
              onChange={(value) => setFormData(prev => ({ ...prev, codPraca: value }))}
              error={!!errors.codPraca}
              helperText={errors.codPraca}
              required
            />
          </Box>

          {/* Endereço */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 2 }}>
            Endereço
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="CEP"
              value={formData.cep}
              onChange={handleInputChange('cep')}
              onBlur={buscaCep}
              placeholder="#####-###"
              slotProps={{
                htmlInput: { maxLength: 9 }
              }}
            />
            
            <TextField
              fullWidth
              label="UF"
              value={formData.uf}
              onChange={handleInputChange('uf')}
              slotProps={{
                htmlInput: { maxLength: 2 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Cidade"
              value={formData.cidade}
              onChange={handleInputChange('cidade')}
              slotProps={{
                htmlInput: { maxLength: 100 }
              }}
            />
            
            <TextField
              fullWidth
              label="Bairro"
              value={formData.bairro}
              onChange={handleInputChange('bairro')}
              slotProps={{
                htmlInput: { maxLength: 100 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Rua"
              value={formData.rua}
              onChange={handleInputChange('rua')}
              slotProps={{
                htmlInput: { maxLength: 200 }
              }}
            />
            
            <TextField
              fullWidth
              label="Número"
              value={formData.numero}
              onChange={handleInputChange('numero')}
              slotProps={{
                htmlInput: { maxLength: 20 }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default VendedorForm;
